<template>
    <div>
        <vue-headful :title="'Root Cause Chart - ' + today + ' :: MeritEHS'" />

        <h4 class="py-3 mb-3 d-print-none">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active">Root Cause Chart</li>
            </ol>
        </h4>

        <div :class="pdf_view ? 'container' : ''" :style="pdf_view ? 'max-width: 980px;' : ''">
            <div class="row d-none d-print-block mb-4" v-if="pdfLogo" style="margin-bottom: -20px; z-index: 100;">
                <div class="col-md-3">
                    <img :src="pdfLogo" class="img-fluid" />
                </div>
            </div>

            <div class="alert alert-info d-print-none" v-if="pdf_view">
                <strong>How to Export as PDF:</strong>
                <ol>
                    <li>On your browser, there should be a "File" or "Options" tab with a "Print" option. You may also hold down the "Ctrl" key and press the "P" key.</li>
                    <li>This will bring up the print page options box. There should be an option called "Select Printer" or "Destination".</li>
                    <li>Select "Save as PDF" or "Adobe PDF".</li>
                    <li>Click "Save" or "Print". This will open your file explorer and let you save it to your computer as a PDF.</li>
                </ol>
            </div>

            <div class="card">
                <div class="card-body">

                    <div class="d-print-none mb-4">
                        <button class="btn btn-sm btn-default" v-on:click="show_filters = !show_filters" title="Show/Hide Filters"><i class="fa fa-filter"></i>
                            <span class="badge" style="background-color: #d9534f" :title="`${filterCount} Filter(s) Applied`" v-if="filterCount > 0">{{ filterCount }}</span>
                        </button>

                        Root Cause Filters:
                        <div v-show="show_filters" class="mt-3">
                            <div class="d-inline-block">
                                <multi-select v-model="filters['hazard_id']" :btnLabel="(values) => ('Hazards ('+values.length+')')" :selectOptions="hazards" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['interval']" :btnLabel="(values) => ('Interval' + (values.length > 0 ? ' - ' + values[0].name : ''))" :selectOptions="intervalOptions" :options="{multi: false}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['IncidentTypeId']" :btnLabel="(values) => ('Incident Types ('+values.length+')')" :selectOptions="incidentTypes" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['InjuryTypeId']" :btnLabel="(values) => ('Injury Types ('+values.length+')')" :selectOptions="injuryTypes" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['DivisionId']" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="divisions" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector ref="rootCauseDate" buttonTitle="Incident Date" v-model="filters['IncidentDate']" />
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['JobsiteId']" :btnLabel="(values) => ('Jobsites ('+values.length+')')" :selectOptions="jobsites" :options="{multi: true}" :search="true"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['behavior_id']" :btnLabel="(values) => ('Behaviors ('+values.length+')')" :selectOptions="behaviors" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['condition_id']" :btnLabel="(values) => ('Conditions ('+values.length+')')" :selectOptions="conditions" :options="{multi: true}"></multi-select>
                            </div>

                            <div style="margin-top: 20px;">
                                <button type="button" class="btn btn-sm btn-default" v-on:click="closeFilters()">
                                    <i class="fa fa-caret-up"></i> Close
                                </button>
                                <button type="button" class="btn btn-sm btn-warning" v-on:click="clearFilters()">
                                    <i class="fa fa-times"></i> Clear
                                </button>
                                <button type="button" class="btn btn-sm btn-success" v-on:click="applyFilters()">
                                    <i class="fa fa-filter"></i> Apply
                                </button>
                                <div class="d-inline-block ml-2">
                                    <b-form-checkbox v-model="pdf_view" :value="true" :unchecked-value="false" plain class="ml-2">PDF View</b-form-checkbox>
                                </div>
                                <div class="d-inline-block ml-2">
                                    <b-form-checkbox v-model="hide_empty" :value="true" :unchecked-value="false" plain class="ml-2">Hide Empty Intervals/Sections</b-form-checkbox>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="loading">
                        <i class="fa fa-spin fa-spinner"></i> Initializing
                    </div>
                    <div v-else>
                        <highcharts id="highcharts-canvas" :options="options" style="min-height: 800px;"/>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import filterExtractor from '@/helpers/TableFilterExtractor';
    import moment from 'moment-timezone';

    export default {
        data: function() {
            return {
                series: [],
                labels: [],
                loading: false,
                hide_empty: false,
                pdf_view: false,
                show_filters: true,
                filterCount: 0,
                pdfLogo: this.$store.state.user.pdf_logo,

                filters: {
                    'interval': [{'name': 'Monthly', 'id': 'Month'}],
                    'IncidentTypeId': [],
                    'InjuryTypeId': [],
                    'DivisionId': [],
                    'IncidentDate' : 'ytd',
                    'JobsiteId': [],
                    'behavior_id': [],
                    'condition_id': [],
                    'hazard_id': [],
                },

                intervalOptions: [
                    {id: 'Week', name: 'Weekly'},
                    {id: 'Month', name: 'Monthly'},
                    {id: 'Year', name: 'Yearly'}
                ],

                hazards: [],
                behaviors: [],
                conditions: [],
                incidentTypes: [],
                injuryTypes: [],
                divisions: [],
                jobsites: [],

                dateLabel: null,
            }
        },

        computed: {
            today(){
                //Send todays date
                return moment().format("YYYY-MM-DD");
            },

            options(){
                return {
                    chart: {
                        type: 'bar'
                    },
                    title: {
                        text: 'Root Cause Analysis: ' + this.dateLabel,
                    },
                    xAxis: {
                        categories: this.labels
                    },
                    yAxis: {
                        title: {
                            text: '# of Incidents'
                        }
                    },
                    plotOptions: {
                        bar: {
                            dataLabels: {
                                enabled: true,
                                formatter: function(){
                                    return this.point.series.name+': '+this.point.y;
                                }
                            }
                        }
                    },
                    legend: {
                        layout: 'horizontal',
                        align: 'right',
                        verticalAlign: 'bottom',
                        x: 0,
                        y: 0,
                        floating: false,
                        borderColor: '#dddddd',
                        borderWidth: 1,
                    },
                    series: this.series,
                }
            }
        },

        watch: {
            hide_empty(newVal, oldValue){
                //Reload the Data
                this.getData();
            },

            pdf_view(newVal, oldVal){
                //Helper?
                this.loading = true;

                //Redraw the Draw
                this.getData();
            },

            'filters.IncidentDate'(newVal, oldVal){
                //Set the label
                this.setDateLabel();
            },
        },

        methods: {
            setDateLabel(){
                //Set the Label
                this.dateLabel = this.$refs.rootCauseDate.getLabel();
            },

            applyFilters() {
                //Update the count
                this.updateFilterCount();

                //Get the data
                this.getData();
            },

            closeFilters() {
                //Hide the modal
                this.show_filters = false;
            },

            updateFilterCount() {
                //Reset the count
                this.filterCount = 0;
                var status = false;

                // Loop over objects in this.filters
                for (var obj in this.filters) {

                    if (this.filters[obj] == null || this.filters[obj] === '') {
                        continue;
                    }

                    if (Array.isArray(this.filters[obj])) {
                        if (this.filters[obj].length > 0) {
                            this.filterCount++;
                        }
                        continue;
                    }

                    if (typeof this.filters[obj] === 'object') {
                        var Empty = true;
                        for (var key in this.filters[obj]) {
                            if (this.filters[obj].hasOwnProperty(key));
                            Empty = false;
                        }
                        if (!Empty) {
                            this.filterCount++;
                        }
                        continue;
                    }

                    if (typeof this.filters[obj] === 'string' || typeof this.filters[obj] === 'number') {
                        this.filterCount++;
                    }
                }
            },

            getData() {
                //Setup
                var filters = filterExtractor.extractFilters(this.filters);

                this.$http
                    .get('reports/root-cause-stats', {filters: filters})
                    .then(response => {
                        if(response.data.series.length === 0)
                        {
                            //Alert the user
                            this.$toastr.warning('There is no data for your currently selected filters.')

                            //Stop here
                            return;
                        }

                        //Set all the data
                        this.labels = response.data.labels;
                        this.series = response.data.series;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            getFilters(){

                //Send the response
                this.$http
                    .get('reports/filters/root-cause', this.filters)
                    .then(response => {
                        //Set all the data
                        this.hazards = response.data.hazards;
                        this.behaviors = response.data.behaviors;
                        this.conditions = response.data.conditions;
                        this.incidentTypes = response.data.incidentTypes;
                        this.divisions = response.data.divisions;
                        this.jobsites = response.data.jobsites;
                        this.injuryTypes = response.data.injuryTypes;

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    })
                    .then(() => {

                    });
            }
        },

        mounted() {
            //Set the Date Label
            this.setDateLabel();

            //Loda the Filters
            this.getFilters();

            //Graph it
            this.getData();
        }
    }
</script>

<style type="text/css" scoped>
    @media print{
        /*@page {
            size: landscape;
        }*/

        #highcharts-canvas {
            width: 2300;
        }

        .card {
            background-color: transparent;
            border: none;
        }
    }
</style>
